/**
 * RaidFrame — R232q22 Phase 5 (Sirus 1:1 UI rebuild).
 *
 * 40-man raid panel — 8 columns × 5 rows of compact unit frames, anchored
 * top:4 left:240 (just to the right of the player frame, NOT centred —
 * matches WoW 3.3.5a default raid layout). z-index 30.
 *
 * Each frame is 80×24 with a 2-px-wide class-coloured stripe down the
 * left edge, an 11-px Cinzel name above a 70×6 HP bar, and a 70×3 power
 * bar below. Portraits are deliberately omitted — raid density wins.
 *
 * Data source:
 *   – `useNet().raidMembers` (array; if missing, treated as empty).
 *   – `useNet().raidMode` boolean. The frame is hidden when `raidMode`
 *     is false OR raidMembers is empty, so it never overlaps the much
 *     denser GroupFrame stack below the player frame.
 *
 * Interactions:
 *   – left-click  → useTarget.setTarget(id)
 *   – right-click → role / kick / etc. context menu (placeholder
 *                   handlers — Colyseus channel for raid commands not
 *                   wired yet; entries log to console for now).
 *
 * Mobile (`@media (pointer: coarse)`): the panel scales to 0.75 so the
 * raid grid fits on a phone without breaking column count.
 *
 * TODO (later passes):
 *   – Real role icons (tank/healer/dps) once the role marker replicates.
 *   – Combat-marker overlay icons (skull, X, square …).
 *   – Loot-roll dialog hook from raid frame ("Need / Greed / Pass").
 */

import { useCallback, useMemo, useState, type CSSProperties, type MouseEvent } from 'react';
import { useNet } from '../../state/net-store.js';
import { useTarget } from '../../state/target-store.js';
import { classById, type ClassId } from '../../data/classes.js';

type ResourceKey = 'mana' | 'rage' | 'focus' | 'energy' | 'runic' | 'faith';

const RESOURCE_TOKEN: Record<ResourceKey, string> = {
  mana: 'var(--aiwari-bar-mana)',
  rage: 'var(--aiwari-bar-rage)',
  focus: 'var(--aiwari-bar-focus)',
  energy: 'var(--aiwari-bar-energy)',
  runic: 'var(--aiwari-bar-runic)',
  faith: 'var(--aiwari-bar-mana)',
};

/** Mini-row schema replicated for raid; identical to GroupMember plus group# */
export interface RaidMember {
  id: string;
  name: string;
  level: number;
  classId: ClassId | string | null;
  raceId: string | null;
  hp: number;
  hpMax: number;
  resource: number;
  resourceMax: number;
  /** Group number 1-8 — drives column placement. */
  group?: number;
  /** Server-replicated role marker; rendered as a tiny dot for now. */
  role?: 'tank' | 'healer' | 'dps' | string;
  /** Greys out the cell when true. */
  offline?: boolean;
  /** Set true on the raid leader (single, distinct from assist). */
  leader?: boolean;
}

// R232q86c: stable empty-array sentinel + split selectors. Same reason as
// GroupFrame — a selector that builds a fresh `{ members, raidMode }` per
// call (or returns `[]` when the field is missing) makes
// useSyncExternalStore think state mutated every render → React #185.
const EMPTY_RAID_MEMBERS: readonly RaidMember[] = Object.freeze([]);

function useRaidMembers(): readonly RaidMember[] {
  return useNet((s) => {
    const raw = (s as unknown as { raidMembers?: unknown }).raidMembers;
    if (!Array.isArray(raw)) return EMPTY_RAID_MEMBERS;
    return raw as RaidMember[];
  });
}

function useRaidMode(members: readonly RaidMember[]): boolean {
  const explicit = useNet(
    (s) => (s as unknown as { raidMode?: unknown }).raidMode as boolean | undefined,
  );
  return typeof explicit === 'boolean' ? explicit : members.length > 0;
}

function getResourceFor(classId: ClassId | string | null | undefined): ResourceKey {
  if (!classId) return 'mana';
  const cls = classById(classId as ClassId);
  if (!cls) return 'mana';
  return cls.resource as ResourceKey;
}

function classColour(classId: ClassId | string | null | undefined): string {
  if (!classId) return 'var(--aiwari-gold)';
  const cls = classById(classId as ClassId);
  return cls?.colour ?? 'var(--aiwari-gold)';
}

interface ContextMenuState {
  x: number;
  y: number;
  member: RaidMember;
}

interface ContextMenuProps {
  state: ContextMenuState;
  onClose: () => void;
  onSetTarget: (id: string) => void;
  onSetRole: (id: string, role: 'tank' | 'healer' | 'dps') => void;
  onKick: (id: string) => void;
  onLeave: () => void;
}

function ContextMenu({ state, onClose, onSetTarget, onSetRole, onKick, onLeave }: ContextMenuProps) {
  const items: Array<{ label: string; action: () => void }> = [
    { label: 'Сделать целью', action: () => onSetTarget(state.member.id) },
    { label: 'Назначить танком', action: () => onSetRole(state.member.id, 'tank') },
    { label: 'Назначить лекарем', action: () => onSetRole(state.member.id, 'healer') },
    { label: 'Назначить ДД', action: () => onSetRole(state.member.id, 'dps') },
    { label: 'Удалить из рейда', action: () => onKick(state.member.id) },
    { label: 'Покинуть рейд', action: () => onLeave() },
  ];
  return (
    <div
      role="menu"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: state.y,
        left: state.x,
        zIndex: 55,
        minWidth: 180,
        padding: 4,
        background: 'var(--aiwari-bg-card-solid)',
        border: '1px solid var(--aiwari-frame-edge)',
        borderRadius: 3,
        boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
        fontFamily: 'var(--aiwari-font-body)',
        fontSize: 12,
        color: 'var(--aiwari-fg)',
      }}
    >
      {items.map((it) => (
        <button
          key={it.label}
          type="button"
          role="menuitem"
          onClick={() => {
            it.action();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '4px 8px',
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            font: 'inherit',
            cursor: 'pointer',
            borderRadius: 2,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'hsl(36 25% 18%)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

interface RaidCellProps {
  member: RaidMember;
  onClick: (id: string) => void;
  onContext: (e: MouseEvent<HTMLDivElement>, m: RaidMember) => void;
}

function RaidCell({ member, onClick, onContext }: RaidCellProps) {
  const stripe = classColour(member.classId);
  const resourceKey = getResourceFor(member.classId);

  const hpPct = member.hpMax > 0 ? Math.max(0, Math.min(100, (member.hp / member.hpMax) * 100)) : 0;
  const resPct = member.resourceMax > 0 ? Math.max(0, Math.min(100, (member.resource / member.resourceMax) * 100)) : 0;
  const opacity = member.offline ? 0.45 : 1;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Рейд: ${member.name}`}
      onClick={() => onClick(member.id)}
      onContextMenu={(e) => onContext(e, member)}
      style={{
        position: 'relative',
        width: 80,
        height: 24,
        padding: '2px 4px 2px 6px',
        boxSizing: 'border-box',
        background: 'linear-gradient(180deg, hsl(36 25% 12%) 0%, hsl(36 25% 7%) 100%)',
        border: '1px solid var(--aiwari-gold-dark)',
        borderRadius: 2,
        opacity,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {/* 2px class-coloured stripe down the left edge */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 2,
          background: stripe,
        }}
      />
      <span
        style={{
          fontFamily: 'var(--aiwari-font-display)',
          fontSize: 'var(--aiwari-text-sm)',
          lineHeight: 1,
          color: 'var(--aiwari-fg)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 70,
        }}
        title={member.name}
      >
        {member.leader ? '[Л] ' : ''}
        {member.name}
      </span>
      <div className="aiwari-bar" style={{ height: 'var(--aiwari-bar-h-thin)', width: 70 }} aria-label={`HP ${Math.round(member.hp)}/${Math.round(member.hpMax)}`}>
        <div className="aiwari-bar-fill" style={{ width: `${hpPct}%`, background: 'var(--aiwari-bar-hp)' }} />
      </div>
      <div className="aiwari-bar" style={{ height: 'calc(var(--aiwari-bar-h-thin) * 0.6)', width: 70 }} aria-label={`Ресурс ${Math.round(member.resource)}/${Math.round(member.resourceMax)}`}>
        <div className="aiwari-bar-fill" style={{ width: `${resPct}%`, background: RESOURCE_TOKEN[resourceKey] }} />
      </div>
    </div>
  );
}

export function RaidFrame() {
  const members = useRaidMembers();
  const raidMode = useRaidMode(members);
  const setTarget = useTarget((s) => s.setTarget);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);

  const onCellClick = useCallback(
    (id: string) => {
      setTarget(id);
    },
    [setTarget],
  );

  const onCellContext = useCallback((e: MouseEvent<HTMLDivElement>, m: RaidMember) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, member: m });
  }, []);

  const onSetTarget = useCallback(
    (id: string) => {
      setTarget(id);
    },
    [setTarget],
  );

  const onSetRole = useCallback((id: string, role: 'tank' | 'healer' | 'dps') => {
    console.info('[RaidFrame] set-role', { id, role });
  }, []);

  const onKick = useCallback((id: string) => {
    console.info('[RaidFrame] kick', id);
  }, []);

  const onLeave = useCallback(() => {
    console.info('[RaidFrame] leave-raid');
  }, []);

  // Bucket members into 8 groups (1-8). Anyone w/o a `group` index falls
  // into bucket 1 — same fallback the WoW client uses for unassigned.
  const groups = useMemo(() => {
    const out: RaidMember[][] = Array.from({ length: 8 }, () => []);
    for (const m of members) {
      const g = Math.max(1, Math.min(8, typeof m.group === 'number' ? m.group : 1));
      if (out[g - 1].length < 5) {
        out[g - 1].push(m);
      }
    }
    return out;
  }, [members]);

  if (!raidMode || members.length === 0) return null;

  const wrapStyle: CSSProperties = {
    position: 'fixed',
    top: 4,
    left: 240,
    zIndex: 30,
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    ['--aiwari-rf-scale' as string]: '1',
    transform: 'scale(var(--aiwari-rf-scale))',
    transformOrigin: 'top left',
  };

  return (
    <>
      <div data-aiwari-raid-frame style={wrapStyle} onClick={() => setMenu(null)}>
        <style>{`
          @media (pointer: coarse) {
            [data-aiwari-raid-frame] { --aiwari-rf-scale: 0.75; }
          }
        `}</style>
        {groups.map((bucket, idx) => {
          if (bucket.length === 0) return null;
          const groupNum = idx + 1;
          return (
            <div
              key={groupNum}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--aiwari-font-caps)',
                  fontSize: 'var(--aiwari-text-xs)',
                  lineHeight: 1,
                  color: 'var(--aiwari-gold-soft)',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                {`Гр. ${groupNum}`}
              </span>
              {bucket.map((m) => (
                <RaidCell key={m.id} member={m} onClick={onCellClick} onContext={onCellContext} />
              ))}
            </div>
          );
        })}
      </div>
      {menu ? (
        <ContextMenu
          state={menu}
          onClose={() => setMenu(null)}
          onSetTarget={onSetTarget}
          onSetRole={onSetRole}
          onKick={onKick}
          onLeave={onLeave}
        />
      ) : null}
    </>
  );
}
